import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ShieldX, Download, Award } from "lucide-react";
import api, { API } from "../lib/api";

export default function CertificateVerify() {
  const { serial } = useParams();
  const [res, setRes] = useState(null);

  useEffect(() => {
    document.title = `Verify certificate ${serial} | Dutsen Kauna Foundation`;
    api.get(`/certificates/${serial}`).then((r) => setRes(r.data)).catch(() => setRes({ valid: false }));
  }, [serial]);

  const c = res?.certificate;
  const sym = { NGN: "₦", USD: "$", GBP: "£", EUR: "€" }[c?.currency] || "";

  return (
    <div data-testid="certificate-verify-page" className="bg-[#f8f4e8] min-h-[70vh] py-16">
      <div className="dk-container max-w-2xl">
        <div className="bg-white rounded-2xl p-8 md:p-10 border border-[#d6aa43]/20">
          {!res ? (
            <p className="text-center text-[#0a0d12]/60">Checking certificate…</p>
          ) : res.valid ? (
            <>
              <div className="flex items-center gap-3 text-green-700 mb-6" data-testid="certificate-valid">
                <ShieldCheck size={28} /> <span className="font-semibold">Authentic certificate issued by Dutsen Kauna Foundation</span>
              </div>
              <div className="rounded-xl bg-[#155e3f] text-white p-6 mb-6">
                <Award className="text-[#d6aa43] mb-2" />
                <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43]">Certificate of Impact</div>
                <div className="font-mono text-xl font-bold text-[#d6aa43] mt-1" data-testid="certificate-serial">{c.serial}</div>
                <div className="font-serif text-2xl font-bold mt-4" data-testid="certificate-donor">{c.donor_name}</div>
                <div className="text-white text-sm">contributed {sym}{Number(c.amount).toLocaleString()} to</div>
                <div className="font-serif text-lg font-bold mt-1" data-testid="certificate-project">{c.project_title}</div>
                <div className="text-white text-xs font-mono">{c.project_serial} · {c.project_location || "Nigeria"}</div>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-sm mb-6">
                <div><dt className="text-[#0a0d12]/60">Issued</dt><dd className="font-semibold">{new Date(c.issued_at).toLocaleDateString()}</dd></div>
                <div><dt className="text-[#0a0d12]/60">Donor reference</dt><dd className="font-mono font-semibold">{c.donor_serial}</dd></div>
                <div><dt className="text-[#0a0d12]/60">Project status</dt><dd className="font-semibold capitalize">{res.project_status?.replace(/_/g, " ") || "Completed"}</dd></div>
              </dl>
              <div className="flex flex-wrap gap-3">
                <a href={`${API}/certificates/${c.serial}/pdf`} target="_blank" rel="noreferrer" className="dk-btn-primary" data-testid="certificate-download">
                  <Download size={16} /> Download PDF
                </a>
                <Link to={`/projects/${c.project_serial}/trace`} className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]" data-testid="certificate-trace-link">View project trace</Link>
              </div>
            </>
          ) : (
            <div className="text-center" data-testid="certificate-invalid">
              <ShieldX size={48} className="text-red-500 mx-auto mb-3" />
              <h1 className="font-serif text-2xl font-bold text-[#0a0d12]">Certificate not found</h1>
              <p className="text-[#0a0d12]/70 mt-2">No certificate exists with number <span className="font-mono">{serial}</span>. If you believe this is an error, contact foundation@dutsenkauna.org.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
